/**
 * Inventory UI — small paper-cartoon cards pinned to the viewport.
 * Always visible at z-99997 (just under cursor / global haze) so the
 * player can see what they're carrying at all times. Updates whenever
 * ParserEngine mutates state.inventory.
 */

import type { ItemArt } from './types.ts';

/** Minimum number of slots always rendered, even when the player is
 *  carrying nothing yet. Empty slots render as outlined placeholder
 *  cards — they make the carry capacity legible from the first frame
 *  ("oh, I have three empty pockets") instead of the bar starting at
 *  zero width and only growing. Items beyond MIN_SLOTS still render. */
const MIN_SLOTS = 3;

export class InventoryUI {
  private root: HTMLElement;
  private currentIds: string[] = [];

  constructor(private catalog: Record<string, ItemArt>) {
    let el = document.getElementById('inventory');
    if (!el) {
      el = document.createElement('div');
      el.id = 'inventory';
      el.className = 'inventory';
      document.body.appendChild(el);
    }
    this.root = el;
  }

  /** Show the inventory bar (called when entering parser mode). */
  show() {
    this.root.classList.remove('hidden');
  }
  /** Hide the inventory bar (called when leaving parser mode). */
  hide() {
    this.root.classList.add('hidden');
  }

  /** Re-render the cards from the given item-id list. */
  set(items: string[]) {
    // Cheap diff — if list hasn't changed, skip.
    if (items.length === this.currentIds.length &&
        items.every((id, i) => id === this.currentIds[i])) return;
    this.currentIds = [...items];
    this.root.innerHTML = '';
    for (const id of items) {
      const art = this.catalog[id];
      if (!art) {
        console.warn(`[inventory] no catalog entry for "${id}"`);
        continue;
      }
      const card = document.createElement('div');
      card.className = 'inv-card';
      const artEl = document.createElement('div');
      artEl.className = 'inv-card-art';
      if (art.imageUrl) {
        const img = document.createElement('img');
        img.src = art.imageUrl;
        img.alt = '';
        artEl.appendChild(img);
      } else {
        // Placeholder — a single Unicode glyph by item id.
        artEl.textContent = INV_PLACEHOLDER[id] ?? '✦';
      }
      const label = document.createElement('div');
      label.className = 'inv-card-label';
      label.textContent = art.label;
      card.appendChild(artEl);
      card.appendChild(label);
      this.root.appendChild(card);
    }
    // Pad up to MIN_SLOTS so the bar always reads as "you have a place
    // for things to go". Empty slots are pure placeholders — no label,
    // no glyph, no click target. Styled by .inv-card.empty in CSS.
    const empties = Math.max(0, MIN_SLOTS - items.length);
    for (let i = 0; i < empties; i++) {
      const card = document.createElement('div');
      card.className = 'inv-card empty';
      card.setAttribute('aria-hidden', 'true');
      const artEl = document.createElement('div');
      artEl.className = 'inv-card-art';
      card.appendChild(artEl);
      this.root.appendChild(card);
    }
  }
}

/** Fallback glyphs for items without art. Add more as you author. */
const INV_PLACEHOLDER: Record<string, string> = {
  coin:   '◉',
  thread: '〜',
  name:   '✦',
};
